import { useNavigate } from 'react-router-dom'
import React, { useState, useEffect, useRef, memo } from 'react'
import {
  IconHeart, IconStar, IconRadio, IconActivity, IconZap,
  IconBattery, IconMeditation, IconStethoscope, IconBarChart,
  IconTrophy, IconLock, IconMoon, IconAlert, IconCheck
} from './components/ui/Icons'

const PRODUCT = 'Manabi'

// ── Canvas ECG — memoized so parent re-renders never reset the animation ──────
// Draws a physiologically correct ECG (P-QRS-T) scrolling left at 25 mm/s.
const LiveECG = memo(function LiveECG({ bpm = 40, color = '#ef4444', height = 64 }) {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const W = canvas.parentElement?.offsetWidth || 288
    canvas.width = W
    const ctx = canvas.getContext('2d')
    const H = canvas.height
    const mid = H / 2

    const BEAT = [
      [0.00,  0.00],
      [0.55,  0.00],
      [0.58,  0.05],
      [0.61,  0.00],
      [0.64, -0.06],
      [0.67,  0.52],
      [0.70, -0.11],
      [0.73,  0.00],
      [0.80,  0.00],
      [0.84,  0.11],
      [0.88,  0.13],
      [0.93,  0.06],
      [1.00,  0.00],
    ]

    const PX_PER_MS = 0.055
    const beatPx    = (60 / bpm) * 1000 * PX_PER_MS

    const grad = ctx.createLinearGradient(0, 0, W, 0)
    grad.addColorStop(0,    `${color}00`)
    grad.addColorStop(0.10, color)
    grad.addColorStop(0.90, color)
    grad.addColorStop(1,    `${color}00`)

    let startMs = null
    let rafId

    function draw(now) {
      if (!startMs) startMs = now
      const scrollPx = ((now - startMs) * PX_PER_MS) % beatPx

      ctx.clearRect(0, 0, W, H)
      ctx.strokeStyle = grad
      ctx.lineWidth   = 2
      ctx.lineJoin    = 'round'
      ctx.lineCap     = 'round'
      ctx.beginPath()

      const firstBeat = Math.floor(-scrollPx / beatPx) - 1
      const lastBeat  = Math.ceil((W - scrollPx) / beatPx) + 1
      let first = true

      for (let b = firstBeat; b <= lastBeat; b++) {
        for (const [t, amp] of BEAT) {
          const x = b * beatPx - scrollPx + t * beatPx
          const y = mid - amp * H
          first ? ctx.moveTo(x, y) : ctx.lineTo(x, y)
          first = false
        }
      }
      ctx.stroke()
      rafId = requestAnimationFrame(draw)
    }

    rafId = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(rafId)
  }, [])

  return (
    <div style={{ overflow: 'hidden', borderRadius: 10, background: 'rgba(0,0,0,0.3)', padding: '6px 0', width: '100%' }}>
      <canvas ref={canvasRef} height={height} style={{ display: 'block', width: '100%' }} />
    </div>
  )
})

// ── Animated live preview card ────────────────────────────────────────────────
function useLiveCardData(initialBpm = 62) {
  const [hr,    setHr]    = useState(initialBpm)
  const [rmssd, setRmssd] = useState(48)
  const [sdnn,  setSdnn]  = useState(64)
  const [state, setState] = useState('Recuperado')
  const [hrKey,    setHrKey]    = useState(0)
  const [rmssdKey, setRmssdKey] = useState(0)
  const [sdnnKey,  setSdnnKey]  = useState(0)
  const hrRef = useRef(initialBpm)

  useEffect(() => {
    const id = setInterval(() => {
      const nextHr = Math.round(Math.max(54, Math.min(74, hrRef.current + (Math.random() - 0.49) * 2.5)))
      hrRef.current = nextHr
      setHr(prev => { if (prev !== nextHr) setHrKey(k => k + 1); return nextHr })

      setRmssd(prev => {
        const next = Math.round(Math.max(38, Math.min(68, prev + (Math.random() - 0.5) * 2.5)))
        if (next !== prev) setRmssdKey(k => k + 1)
        return next
      })
      setSdnn(prev => {
        const next = Math.round(Math.max(48, Math.min(78, prev + (Math.random() - 0.5) * 2)))
        if (next !== prev) setSdnnKey(k => k + 1)
        return next
      })
      setState(nextHr < 60 ? 'Calma profunda' : nextHr < 70 ? 'Recuperado' : 'Activo')
    }, 3200)
    return () => clearInterval(id)
  }, [])

  return { hr, hrKey, rmssd, rmssdKey, sdnn, sdnnKey, state }
}

// ── Beating dot ──────────────────────────────────────────────────────────────
function LiveDot() {
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: '#ef4444', display: 'inline-block',
        animation: 'livePulse 1.4s ease infinite',
      }} />
      <span style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', letterSpacing: 0.5 }}>EN VIVO</span>
    </span>
  )
}

// ── Pricing card ─────────────────────────────────────────────────────────────
function PlanCard({ plan, highlighted, onCTA }) {
  const [hover, setHover] = useState(false)
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: highlighted ? '#111827' : '#fff',
        border: highlighted ? '2px solid #111827' : '2px solid #e5e7eb',
        borderRadius: 20, padding: '32px 28px',
        display: 'flex', flexDirection: 'column', gap: 20,
        transform: highlighted ? 'scale(1.04)' : hover ? 'translateY(-4px)' : 'none',
        transition: 'all 0.2s',
        boxShadow: highlighted ? '0 24px 64px rgba(17,24,39,0.25)' : hover ? '0 8px 32px rgba(0,0,0,0.1)' : '0 2px 8px rgba(0,0,0,0.06)',
        position: 'relative',
      }}>
      {highlighted && (
        <div style={{
          position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
          background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 800,
          padding: '5px 18px', borderRadius: 20, whiteSpace: 'nowrap', letterSpacing: 0.8,
          display: 'flex', alignItems: 'center', gap: 6
        }}>
          <IconStar size={12} color="#fff" fill="#fff" />
          <span>MÁS ELEGIDO</span>
        </div>
      )}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{plan.icon}</div>
          <span style={{ fontSize: 13, fontWeight: 700, color: highlighted ? '#9ca3af' : '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>{plan.name}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, marginBottom: 4 }}>
          {plan.price === 0
            ? <span style={{ fontSize: 44, fontWeight: 900, color: highlighted ? '#fff' : '#111827', letterSpacing: -2 }}>Gratis</span>
            : <>
                <span style={{ fontSize: 15, fontWeight: 700, color: highlighted ? '#9ca3af' : '#6b7280', marginBottom: 12 }}>USD</span>
                <span style={{ fontSize: 48, fontWeight: 900, color: highlighted ? '#fff' : '#111827', letterSpacing: -2, lineHeight: 1 }}>{plan.price}</span>
                <span style={{ fontSize: 14, color: highlighted ? '#9ca3af' : '#6b7280', marginBottom: 10 }}>/mes</span>
              </>
          }
        </div>
        <p style={{ fontSize: 13, color: highlighted ? '#d1d5db' : '#6b7280' }}>{plan.tagline}</p>
      </div>

      <button onClick={onCTA} style={{
        background: highlighted ? '#ef4444' : '#111827',
        color: '#fff', border: 'none', borderRadius: 12,
        padding: '13px', fontSize: 14, fontWeight: 700,
        cursor: 'pointer', fontFamily: 'inherit',
        transition: 'opacity 0.15s',
      }}>
        {plan.cta}
      </button>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {plan.features.map((f, i) => (
          <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: highlighted ? '#e5e7eb' : '#374151', lineHeight: 1.5 }}>
            <span style={{ color: '#10b981', fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
            {f}
          </li>
        ))}
        {plan.notIncluded?.map((f, i) => (
          <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: '#9ca3af', lineHeight: 1.5 }}>
            <span style={{ flexShrink: 0, marginTop: 1 }}>—</span>
            {f}
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Feature row ──────────────────────────────────────────────────────────────
function Feature({ icon, title, body }) {
  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ width: 46, height: 46, borderRadius: 12, background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </div>
      <div>
        <p style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 }}>{title}</p>
        <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6 }}>{body}</p>
      </div>
    </div>
  )
}

// ── Heartbeat orb background ─────────────────────────────────────────────────
function HeartOrb() {
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0 }}>
      <div style={{ position: 'absolute', inset: 0, background: '#07070f' }} />
      <div style={{
        position: 'absolute',
        top: '50%', left: '50%',
        width: 900, height: 900,
        transform: 'translate(-50%, -50%)',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(239,68,68,0.18) 0%, rgba(239,68,68,0.06) 45%, transparent 70%)',
        filter: 'blur(80px)',
        animation: 'orbBreath 3.2s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute',
        top: '50%', left: '50%',
        width: 480, height: 480,
        transform: 'translate(-50%, -50%)',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(239,68,68,0.55) 0%, rgba(239,68,68,0.22) 38%, transparent 68%)',
        filter: 'blur(40px)',
        animation: 'orbBeat 2s ease-in-out infinite',
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(7,7,15,0.35) 0%, rgba(7,7,15,0.75) 60%, rgba(7,7,15,0.97) 100%)',
      }} />
    </div>
  )
}

// ── Animated live preview card ────────────────────────────────────────────────
function LivePreviewCard() {
  const { hr, hrKey, rmssd, rmssdKey, sdnn, sdnnKey, state } = useLiveCardData(62)

  const metrics = [
    { label: 'RMSSD (Vagal)', value: `${rmssd} ms`, color: '#10b981', k: rmssdKey },
    { label: 'SDNN (Estrés)', value: `${sdnn} ms`,  color: '#3b82f6', k: sdnnKey  },
    { label: 'Estado',       value: state,          color: '#8b5cf6', k: hrKey    },
  ]

  return (
    <div>
      <div style={{
        background: 'rgba(17,24,39,0.85)', backdropFilter: 'blur(20px)',
        border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: 28, padding: 24,
        width: '100%', maxWidth: 350, boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <p style={{ fontSize: 11, color: '#9ca3af', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>Ritmo en Tiempo Real</p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span
                key={hrKey}
                style={{
                  fontSize: 56, fontWeight: 900, color: '#fff', letterSpacing: -2, lineHeight: 1,
                  display: 'inline-block',
                  animation: hrKey > 0 ? 'numIn 0.55s cubic-bezier(0.22,1,0.36,1) both' : 'none',
                }}
              >{hr}</span>
              <span style={{ fontSize: 14, color: '#9ca3af', fontWeight: 600 }}>BPM</span>
            </div>
          </div>
          <LiveDot />
        </div>

        <LiveECG bpm={40} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 14 }}>
          {metrics.map(({ label, value, color, k }) => (
            <div key={label} style={{ background: 'rgba(31,41,55,0.8)', borderRadius: 10, padding: '9px 10px' }}>
              <p style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</p>
              <p
                key={k}
                style={{
                  fontSize: 13.5, fontWeight: 700, color,
                  animation: k > 0 ? 'numInSm 0.45s cubic-bezier(0.22,1,0.36,1) both' : 'none',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                }}
              >{value}</p>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 12, background: 'rgba(31,41,55,0.8)', borderRadius: 12, padding: '11px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#374151', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconRadio size={14} color="#10b981" />
            </div>
            <div>
              <p style={{ fontSize: 11, color: '#e5e7eb', fontWeight: 600 }}>Polar H10 conectado</p>
              <p style={{ fontSize: 10, color: '#9ca3af' }}>130 Hz ECG · Telemetría 24/7</p>
            </div>
          </div>
          <span style={{ fontSize: 10, background: '#065f46', color: '#6ee7b7', padding: '3px 8px', borderRadius: 10, fontWeight: 700 }}>ÓPTIMO</span>
        </div>
      </div>
    </div>
  )
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function Landing() {
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const pricingRef = useRef(null)

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 60)
    window.addEventListener('scroll', h)
    return () => window.removeEventListener('scroll', h)
  }, [])

  const plans = [
    {
      name: 'Salud & Diario',
      icon: <IconActivity size={22} color="#10b981" />,
      price: 0,
      tagline: 'Cuidá y controlá tu día a día',
      cta: 'Empezar gratis',
      features: ['Monitoreo fisiológico en vivo', 'Historial continuo de 3 días', 'FC promedio, mínimas y máximas', 'ECG clínico de 130 Hz en vivo', 'Competencia de latidos y pasos con amigos'],
      notIncluded: ['Recovery score & fatiga profunda', 'Módulo de respiración & biofeedback', 'API de Streaming para OBS y Twitch'],
    },
    {
      name: 'Calidad de Vida',
      icon: <IconZap size={22} color="#f59e0b" />,
      price: 9,
      tagline: 'Para optimizar tu descanso y energía',
      cta: 'Elegir Calidad de Vida',
      features: ['Todo lo del plan Salud', 'Historial y evolución sin límites', 'Detección de fatiga y cansancio acumulado', 'Aviso inteligente: cuándo entrenar o descansar', 'Biofeedback para meditación guiada', 'Análisis de estrés autónomo (RMSSD/SDNN)'],
    },
    {
      name: 'Pro & Creadores',
      icon: <IconStar size={22} color="#ef4444" fill="#ef4444" />,
      price: 19,
      tagline: 'Biohackers, atletas y streamers',
      cta: 'Elegir Pro & Creadores',
      features: ['Todo lo del plan Calidad de Vida', 'API de Streaming en vivo (OBS / Twitch / Kick)', 'Widget transparente para transmisiones', 'Acceso médico o preparador físico sin clave', 'Exportación médica PDF + CSV milimétrico', 'Análisis espectral LF/HF y Poincaré'],
    },
  ]

  const scrollToPricing = () => pricingRef.current?.scrollIntoView({ behavior: 'smooth' })

  return (
    <div style={{ background: '#fff', minHeight: '100vh', color: '#111827', fontFamily: '-apple-system,BlinkMacSystemFont,"SF Pro Display",sans-serif' }}>
      <style>{`
        @keyframes livePulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.5);opacity:0.6} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:none} }
        @keyframes orbBreath {
          0%,100% { transform:translate(-50%,-50%) scale(1);   opacity:0.8; }
          50%      { transform:translate(-50%,-50%) scale(1.12); opacity:1; }
        }
        @keyframes orbBeat {
          0%   { transform:translate(-50%,-50%) scale(1);    opacity:0.6; }
          8%   { transform:translate(-50%,-50%) scale(1.22); opacity:1;   }
          16%  { transform:translate(-50%,-50%) scale(0.96); opacity:0.75;}
          26%  { transform:translate(-50%,-50%) scale(1.13); opacity:0.9; }
          38%  { transform:translate(-50%,-50%) scale(1);    opacity:0.6; }
          100% { transform:translate(-50%,-50%) scale(1);    opacity:0.6; }
        }
        @keyframes numIn   { from { opacity:0; transform:translateY(7px) } to { opacity:1; transform:none } }
        @keyframes numInSm { from { opacity:0; transform:translateY(4px) } to { opacity:1; transform:none } }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: #fecaca; }

        .nav-prices  { display: inline-block; }
        .nav-cta     { display: inline-block; }
        .hero-card   { display: flex; justify-content: center; }
        @media (max-width: 640px) {
          .nav-prices { display: none; }
          .hero-card  { margin-top: 10px; }
        }
      `}</style>

      {/* ── NAVBAR ── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px clamp(16px, 5vw, 64px)',
        background: scrolled ? 'rgba(255,255,255,0.92)' : 'transparent',
        backdropFilter: scrolled ? 'blur(16px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(0,0,0,0.08)' : 'none',
        transition: 'all 0.3s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconHeart size={22} color="#ef4444" fill="#ef4444" />
          <span style={{ fontSize: 18, fontWeight: 900, color: scrolled ? '#111827' : '#fff', letterSpacing: -0.5, transition: 'color 0.3s' }}>{PRODUCT}</span>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className="nav-prices" onClick={scrollToPricing} style={{ background: 'none', border: 'none', color: scrolled ? '#6b7280' : 'rgba(255,255,255,0.7)', fontSize: 14, cursor: 'pointer', padding: '8px 12px', borderRadius: 8, fontFamily: 'inherit', fontWeight: 500 }}>
            Planes
          </button>
          <button className="nav-login" onClick={() => navigate('/login')} style={{ background: 'none', border: `1.5px solid ${scrolled ? '#e5e7eb' : 'rgba(255,255,255,0.2)'}`, color: scrolled ? '#374151' : '#fff', fontSize: 14, cursor: 'pointer', padding: '7px 16px', borderRadius: 10, fontFamily: 'inherit', fontWeight: 600 }}>
            Ingresar
          </button>
          <button className="nav-cta" onClick={() => navigate('/register')} style={{ background: '#ef4444', border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', padding: '8px 18px', borderRadius: 10, fontFamily: 'inherit' }}>
            Empezar gratis
          </button>
        </div>
      </nav>

      {/* ── HERO: ENFOCADO EN MEJORAR LA CALIDAD DE VIDA ── */}
      <section style={{ position: 'relative', overflow: 'hidden' }}>
        <HeartOrb />
        <div style={{
          position: 'relative', zIndex: 1,
          paddingTop: 'clamp(100px, 14vw, 140px)',
          paddingBottom: 'clamp(80px, 10vw, 120px)',
          paddingLeft: 'clamp(16px, 5vw, 64px)',
          paddingRight: 'clamp(16px, 5vw, 64px)',
          maxWidth: 1200, margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 64, alignItems: 'center',
          animation: 'fadeUp 0.6s ease both',
        }}>
        {/* Left */}
        <div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 20, padding: '6px 14px', marginBottom: 24 }}>
            <LiveDot />
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>El guardián de tu salud 24/7</span>
          </div>

          <h1 style={{ fontSize: 'clamp(36px, 5.5vw, 62px)', fontWeight: 900, lineHeight: 1.08, letterSpacing: -2, color: '#fff', marginBottom: 20 }}>
            Mejorá tu calidad de vida.<br />
            Entendé tu cuerpo<br />
            <span style={{ color: '#ef4444' }}>las 24 horas del día.</span>
          </h1>

          <p style={{ fontSize: 'clamp(16px, 2vw, 18.5px)', color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, marginBottom: 36, maxWidth: 490 }}>
            Conectá tu Polar H10 y descubrí la verdad sobre tu salud. Manabi te avisa si estás fatigado o estresado, te guía cuándo es el momento óptimo para entrenar, potencia tu meditación y te permite compartir tu pulso en vivo en tus transmisiones.
          </p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 36 }}>
            <button onClick={() => navigate('/register')} style={{
              background: '#ef4444', border: 'none', color: '#fff',
              fontSize: 16, fontWeight: 700, cursor: 'pointer',
              padding: '15px 32px', borderRadius: 14, fontFamily: 'inherit',
              boxShadow: '0 4px 32px rgba(239,68,68,0.5)',
            }}>
              Comenzar ahora gratis →
            </button>
            <button onClick={scrollToPricing} style={{
              background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(255,255,255,0.18)', color: '#fff',
              fontSize: 16, fontWeight: 600, cursor: 'pointer',
              padding: '15px 28px', borderRadius: 14, fontFamily: 'inherit',
              backdropFilter: 'blur(8px)',
            }}>
              Ver planes
            </button>
          </div>

          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {[['Sin tarjeta requerida', '✓'], ['Cancelación en 1 clic', '✓'], ['Datos 100% tuyos y privados', '✓']].map(([t, icon]) => (
              <span key={t} style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ color: '#10b981', fontWeight: 700 }}>{icon}</span> {t}
              </span>
            ))}
          </div>
        </div>

        {/* Right — recuadro interactivo */}
        <div className="hero-card"><LivePreviewCard /></div>
        </div>
      </section>

      {/* ── PILARES DE SALUD Y CALIDAD DE VIDA ── */}
      <section style={{ background: '#f9fafb', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 5vw, 64px)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 2, textAlign: 'center', marginBottom: 10 }}>Propósito</p>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 900, color: '#111827', textAlign: 'center', marginBottom: 52, letterSpacing: -1 }}>
            Diseñado para transformar<br />cómo cuidás tu salud
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
            {[
              {
                icon: <IconBattery size={26} color="#10b981" />, color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0',
                title: 'Control de Fatiga y Recuperación',
                pill: 'Evolución Diaria',
                body: 'Manabi analiza tu sistema nervioso autónomo las 24 hs. Sabé exactamente si tu cuerpo acumuló fatiga o cansancio y si necesitás descansar antes de desgastarte.',
                items: ['Semáforo de energía: cuándo entrenar y cuándo frenar', 'Índice de recuperación matutina (Recovery Score)', 'Evaluación del impacto del sueño y el estrés diario'],
              },
              {
                icon: <IconMeditation size={26} color="#8b5cf6" />, color: '#8b5cf6', bg: '#faf5ff', border: '#ddd6fe',
                title: 'Meditación & Biofeedback',
                pill: 'Calma y Respiración',
                body: 'El corazón no miente. Usá tu Polar H10 durante tus sesiones de meditación o respiración para ver en pantalla cómo tu mente toma el control de tu sistema nervioso.',
                items: ['Biofeedback visual inmediato de tus latidos', 'Seguimiento del tono vagal en tiempo real (RMSSD)', 'Calma profunda para desconectar del estrés laboral'],
              },
              {
                icon: <IconRadio size={26} color="#ef4444" />, color: '#ef4444', bg: '#fff5f5', border: '#fecaca',
                title: 'API en Vivo para Creadores',
                pill: 'OBS Studio & Streaming',
                body: 'Llevá tu contenido al siguiente nivel. Transmití tus pulsaciones reales a Twitch, YouTube o Kick con nuestro widget transparente o integralo en tu propia web con nuestra API.',
                items: ['Widget transparente listo para Browser Source en OBS', 'API REST y Server-Sent Events (SSE) para desarrolladores', 'Mostrá tu emoción y estrés en vivo durante tus streams'],
              },
            ].map((s) => (
              <div key={s.title} style={{ background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: 20, padding: '28px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 10, background: '#fff', border: `1px solid ${s.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {s.icon}
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: s.color, background: 'white', padding: '3px 10px', borderRadius: 20, border: `1px solid ${s.border}` }}>{s.pill}</span>
                </div>
                <p style={{ fontSize: 17, fontWeight: 800, color: '#111827', marginBottom: 10, letterSpacing: -0.3 }}>{s.title}</p>
                <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.65, marginBottom: 16 }}>{s.body}</p>
                <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {s.items.map(item => (
                    <li key={item} style={{ fontSize: 13, color: '#374151', display: 'flex', gap: 8 }}>
                      <span style={{ color: s.color, fontWeight: 700 }}>✓</span> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MÓDULO OBS & STREAMERS ── */}
      <section style={{ background: '#0a0a0f', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 5vw, 64px)', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 48, alignItems: 'center' }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(239,68,68,0.14)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 20, padding: '5px 14px', marginBottom: 20 }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: '#f87171', textTransform: 'uppercase', letterSpacing: 2 }}>Exclusivo Premium</span>
            </div>
            <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 900, color: '#fff', letterSpacing: -1, lineHeight: 1.15, marginBottom: 16 }}>
              Tu pulso real en OBS.<br /><span style={{ color: '#ef4444' }}>Para streamers y creadores.</span>
            </h2>
            <p style={{ fontSize: 16, color: '#9ca3af', lineHeight: 1.7, marginBottom: 24, maxWidth: 480 }}>
              Mantené a tu audiencia al borde del asiento mostrando tu frecuencia cardíaca en vivo durante tus partidas, desafíos físicos o charlas. Copiá tu enlace de widget, pegalo en OBS Studio como fuente de navegador y listo: fondo transparente automático y cero configuración extra.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32 }}>
              {['Widget transparente con pulso y latidos en vivo', 'API pública en tiempo real (SSE y JSON) para desarrolladores', 'Totalmente personalizable y compatible con Streamlabs, Twitch y YouTube'].map(t => (
                <li key={t} style={{ fontSize: 14, color: '#d1d5db', display: 'flex', gap: 10 }}>
                  <span style={{ color: '#10b981', fontWeight: 700 }}>✓</span> {t}
                </li>
              ))}
            </ul>
            <button onClick={() => navigate('/register')} style={{
              background: '#ef4444', border: 'none', color: '#fff',
              fontSize: 15, fontWeight: 700, cursor: 'pointer',
              padding: '14px 30px', borderRadius: 14, fontFamily: 'inherit',
              boxShadow: '0 4px 32px rgba(239,68,68,0.4)',
            }}>
              Obtener mi Stream Key →
            </button>
          </div>

          {/* Maqueta OBS */}
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{
              background: 'rgba(23, 23, 31, 0.95)', border: '1.5px solid rgba(255,255,255,0.12)',
              borderRadius: 22, padding: 24, width: 'min(380px, 100%)', boxShadow: '0 24px 80px rgba(0,0,0,0.6)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1 }}>OBS Browser Source</span>
                <span style={{ fontSize: 10, background: '#ef4444', color: '#fff', padding: '2px 8px', borderRadius: 8, fontWeight: 800 }}>LIVE WIDGET</span>
              </div>
              <div style={{ background: '#0a0a0f', borderRadius: 14, padding: '16px 20px', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                <IconHeart size={32} color="#ef4444" fill="#ef4444" />
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 38, fontWeight: 900, color: '#fff', letterSpacing: -1 }}>134</span>
                    <span style={{ fontSize: 12, color: '#9ca3af', fontWeight: 700 }}>BPM</span>
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 800, color: '#ff9f0a', background: 'rgba(255,159,10,0.15)', padding: '2px 6px', borderRadius: 4 }}>CARDIO INTENSO</span>
                </div>
              </div>
              <p style={{ fontSize: 11.5, color: '#6b7280', margin: 0, textAlign: 'center' }}>
                Fondo transparente nativo · Sin marcas de agua · 0 latencia
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FUNCIONES DE SALUD Y POLAR H10 ── */}
      <section style={{ padding: 'clamp(60px, 8vw, 100px) clamp(16px, 5vw, 64px)', maxWidth: 1100, margin: '0 auto' }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 2, textAlign: 'center', marginBottom: 10 }}>Todo en una sola app</p>
        <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 900, color: '#111827', textAlign: 'center', marginBottom: 52, letterSpacing: -1 }}>
          El máximo provecho de tu Polar H10<br />para tu bienestar diario
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 36 }}>
          <Feature icon={<IconStethoscope size={22} color="#10b981" />} title="Electrocardiograma a 130 Hz" body="Visualizá tu tira de ECG clínica en alta resolución. Revisá la onda P-QRS-T real y reconocé cualquier latido anómalo o arritmia." />
          <Feature icon={<IconBattery size={22} color="#10b981" />} title="Índice de fatiga y cansancio" body="Descubrí si estás sobreentrenado o si tu sistema nervioso necesita una pausa reparadora antes de seguir exigiéndote." />
          <Feature icon={<IconMeditation size={22} color="#8b5cf6" />} title="Biofeedback en Meditación" body="Visualizá en pantalla grande cómo la respiración lenta disminuye tus pulsaciones y estabiliza tu variabilidad cardíaca." />
          <Feature icon={<IconBarChart size={22} color="#007aff" />} title="HRV profundo y preciso" body="Métricas clínicas: RMSSD, SDNN, pNN50, frecuencia respiratoria estimada y gráfico Poincaré de dinámica no lineal." />
          <Feature icon={<IconRadio size={22} color="#ef4444" />} title="API Streaming para Creadores" body="Conectá tu pulso con OBS Studio o desarrollá tus propias integraciones gracias a nuestra API en tiempo real." />
          <Feature icon={<IconTrophy size={22} color="#f0b429" />} title="Competí con amigos" body="Compartí un código de grupo con tus amigos. Cada paso y cada latido registrado suma en el ranking semanal y mensual." />
          <Feature icon={<IconLock size={22} color="#6b7280" />} title="Tus datos, siempre privados" body="Aislamiento total de inquilinos en base de datos. Exportación completa en CSV para Excel, Python o tu médico de cabecera." />
          <Feature icon={<IconMoon size={22} color="#6366f1" />} title="Modo noche integral" body="Interfaz clínica oscura diseñada para no encandilar tus ojos cuando medís antes de dormir o durante la madrugada." />
          <Feature icon={<IconAlert size={22} color="#ff9500" />} title="Respaldo sin conexión" body="Si perdés la señal Wi-Fi, tus sesiones se resguardan de forma segura en tu teléfono y se suben solas al reconectar." />
        </div>
      </section>

      {/* ── PRICING ── */}
      <section ref={pricingRef} id="pricing" style={{ background: '#f9fafb', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 5vw, 64px)' }}>
        <div style={{ maxWidth: 1050, margin: '0 auto' }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 2, textAlign: 'center', marginBottom: 10 }}>Planes</p>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 900, color: '#111827', textAlign: 'center', marginBottom: 10, letterSpacing: -1 }}>
            Elegí cómo cuidar tu salud
          </h2>
          <p style={{ fontSize: 15, color: '#6b7280', textAlign: 'center', marginBottom: 52 }}>
            Empezá gratis con tu Polar H10. Mejorá tu plan cuando quieras potenciar tu calidad de vida.
          </p>
          <div className="pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, alignItems: 'center' }}>
            {plans.map((p, i) => <PlanCard key={i} plan={p} highlighted={i === 1} onCTA={() => navigate('/register')} />)}
          </div>
          <p style={{ textAlign: 'center', marginTop: 28, fontSize: 13, color: '#9ca3af' }}>
            Precios transparentes · Procesado seguro con MercadoPago · Cancelás con un clic
          </p>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section style={{ background: '#111827', padding: 'clamp(60px, 8vw, 100px) clamp(16px, 5vw, 64px)', textAlign: 'center' }}>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <IconHeart size={44} color="#ef4444" fill="#ef4444" />
          </div>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 900, color: '#fff', margin: '20px 0 16px', letterSpacing: -1 }}>
            Cuidá tu salud hoy mismo
          </h2>
          <p style={{ fontSize: 16, color: '#9ca3af', lineHeight: 1.65, marginBottom: 36 }}>
            Sin tarjeta de crédito. Vinculá tu Polar H10 en 30 segundos y tomá el control de tu evolución.
          </p>
          <button onClick={() => navigate('/register')} style={{
            background: '#ef4444', border: 'none', color: '#fff',
            fontSize: 18, fontWeight: 700, cursor: 'pointer',
            padding: '18px 48px', borderRadius: 16, fontFamily: 'inherit',
            boxShadow: '0 8px 32px rgba(239,68,68,0.4)',
          }}>
            Crear cuenta gratis →
          </button>
          <p style={{ fontSize: 13, color: '#4b5563', marginTop: 16 }}>
            Listo en 1 minuto. Funciona en tu navegador favorito.
          </p>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ borderTop: '1px solid #1f2937', background: '#111827', padding: '28px clamp(16px, 5vw, 64px)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconHeart size={16} color="#ef4444" fill="#ef4444" />
          <span style={{ fontSize: 14, fontWeight: 800, color: '#fff' }}>{PRODUCT}</span>
        </div>
        <p style={{ fontSize: 12, color: '#4b5563' }}>
          <a href="/terminos" style={{ color: 'inherit', textDecoration: 'underline' }}>Términos y condiciones</a> · Privacidad garantizada
        </p>
        <div style={{ display: 'flex', gap: 16 }}>
          <button onClick={() => navigate('/login')} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Ingresar</button>
          <button onClick={() => navigate('/register')} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700 }}>Registrarse →</button>
        </div>
      </footer>
    </div>
  )
}
